import _ from "lodash";
import ACLStore from "lib/aclStore";
import isTypingInInput from "Utils/isTypingInInput";
import { getSettingValue } from "modules/PermissionModule";
import {
  ENABLE_GLOBAL_HEADER,
  ENABLE_ALL_SHORTCUTS,
  ENABLE_SHORTCUT_PROMPTS,
} from "Constants/permissionConstants";

// ---------------------------------------------- START: academic year curriculum mapping ----------------------------------------------

const getCurriculumProgramOrderById = ({ curriculumPrograms }) => {
  return _.reduce(
    curriculumPrograms,
    (result, { id }, index) => {
      return { ...result, [id]: index };
    },
    {}
  );
};

export const getCurriculumProgramOrderByIdMemoize = _.memoize(
  params => getCurriculumProgramOrderById(params),
  ({ curriculumPrograms }) => JSON.stringify(_.map(curriculumPrograms, "id"))
);

export const getSortedCurriculums = ({
  curriculumPrograms,
  curriculumProgramOrderById,
}) => {
  return _.sortBy(curriculumPrograms, ({ id }) => {
    return curriculumProgramOrderById[id];
  });
};

export const getSortedCurriculumsMemoize = _.memoize(
  params => getSortedCurriculums(params),
  ({ curriculumPrograms, curriculumProgramOrderById }) =>
    JSON.stringify({
      curriculumPrograms: _.map(curriculumPrograms, ({ id, label }) => ({
        id,
        label,
      })),
      curriculumProgramOrderById,
    })
);

const EMPTY_ARRAY = [];

const getStaffUserDetails = ({
  staffAcademicYearCurriculumMap = [],
  academicYearId,
  organizationCurriculumPrograms,
}) => {
  const targetEntry = _.find(
    staffAcademicYearCurriculumMap,
    entry => _.get(entry, "academicYear.id") === academicYearId
  );

  const curriculumProgramOrderById = getCurriculumProgramOrderByIdMemoize({
    curriculumPrograms: organizationCurriculumPrograms ?? [],
  });

  const userCurriculumPrograms = getSortedCurriculumsMemoize({
    curriculumPrograms: _.get(targetEntry, "curriculumPrograms", EMPTY_ARRAY),
    curriculumProgramOrderById,
  });

  return { userCurriculumPrograms };
};

export const getStaffUserDetailsMemoize = _.memoize(
  getStaffUserDetails,
  ({
    academicYearId,
    staffAcademicYearCurriculumMap,
    organizationCurriculumPrograms,
  }) =>
    `${academicYearId}_${_.map(organizationCurriculumPrograms, "id").join(
      ","
    )}_${_.map(
      staffAcademicYearCurriculumMap,
      entry =>
        `${_.get(entry, "academicYear.id")}:${_.map(
          entry.curriculumPrograms,
          "id"
        ).join(",")}`
    ).join("|")}`
);

const getSortedMapping = ({ mapping, curriculumProgramOrderById }) => {
  const sortedMapping = {};

  const academicYearIds = _.keys(mapping);

  _.forEach(academicYearIds, academicYearId => {
    const curriculumPrograms = _.get(
      mapping,
      [academicYearId, "curriculumPrograms"],
      []
    );

    sortedMapping[academicYearId] = {
      ...mapping[academicYearId],
      // sort the curriculumPrograms array
      curriculumPrograms: getSortedCurriculums({
        curriculumPrograms,
        curriculumProgramOrderById,
      }),
    };
  });

  return sortedMapping;
};

const getUserAcadYearConstants = ({
  allCourses,
  orgAcademicYears,
  organizationCurriculumPrograms,
}) => {
  const mapping = {};
  const currentOrgAcademicYearId = _.get(
    _.find(orgAcademicYears, {
      isCurrentAcademicYear: true,
    }),
    "id"
  );

  // loop over allCourses and create a mapping object
  _.forEach(allCourses, course => {
    const academicYears = _.get(course, "academicYears");
    const length = _.size(academicYears);

    // One course can be inside multiple academic years, hence loop over all academic years
    for (let i = 0; i < length; i++) {
      // get academic year id
      const academicYearId = _.get(academicYears, [i, "id"]);

      const courseCurriculum = _.get(course, "curriculumProgram", {});

      const hasAcademicYearIdKey = mapping[academicYearId];
      if (!_.isEmpty(courseCurriculum)) {
        if (!hasAcademicYearIdKey) {
          // if academic year object does not exist, then create it
          mapping[academicYearId] = {
            curriculumPrograms: [],
            isCurrentAcademicYear: null,
          };
        }

        mapping[academicYearId].curriculumPrograms = _.union(
          _.get(mapping, [academicYearId, "curriculumPrograms"]),
          [courseCurriculum]
        );

        mapping[academicYearId].isCurrentAcademicYear =
          academicYearId === currentOrgAcademicYearId;
      }
    }
  });

  const academicYearIds = _.keys(mapping);

  _.forEach(academicYearIds, academicYearId => {
    const { curriculumPrograms } = mapping[academicYearId];
    mapping[academicYearId].curriculumPrograms = _.map(
      curriculumPrograms,
      ({ id: curriculumProgramId, ...rest }) => {
        const subscriptionPlan = _.get(
          _.find(
            organizationCurriculumPrograms,
            ({ id }) => id == curriculumProgramId
          ),
          "subscriptionPlan",
          {}
        );
        return { id: curriculumProgramId, ...rest, subscriptionPlan };
      }
    );
  });

  const curriculumProgramOrderById = getCurriculumProgramOrderById({
    curriculumPrograms: organizationCurriculumPrograms ?? [],
  });

  const academicYearCurriculumProgramsMapping = getSortedMapping({
    mapping,
    curriculumProgramOrderById,
  });

  return {
    academicYearCurriculumProgramsMapping,
    currentAcadYearObj:
      academicYearCurriculumProgramsMapping[currentOrgAcademicYearId] || {},
  };
};

export const getUserAcadYearConstantsMemoize = _.memoize(
  params => getUserAcadYearConstants(params),
  ({ allCourses, orgAcademicYears, organizationCurriculumPrograms }) =>
    JSON.stringify({
      allCourses: _.map(allCourses, course => ({
        id: course?.id,
        academicYears: _.map(course?.academicYears, "id"),
      })),
      orgAcademicYears: _.map(orgAcademicYears, "id"),
      orgCurriculumPrograms: _.map(
        organizationCurriculumPrograms,
        curriculumProgram => ({
          id: curriculumProgram.id,
          label: curriculumProgram.label,
        })
      ),
    })
);

const getAdminPageTitle = ({ curriculumTypeName, pageTitleInfo, t }) => {
  const label = pageTitleInfo?.label;
  const secondLabel = pageTitleInfo?.secondLabel;
  const withCurriculumProgram = pageTitleInfo?.withCurriculumProgram;
  const isSecondLabel =
    !_.isEqual(label, secondLabel) && !_.isEmpty(secondLabel);

  let title = "";

  if (withCurriculumProgram) {
    title += curriculumTypeName;
  }

  if (label) {
    title += ` ${t(label)}`;
  }

  if (isSecondLabel) {
    title += ` - ${t(secondLabel)}`;
  }

  return title;
};

const getHomePageTitle = ({ pageTitleInfo, curriculumTypeName, t }) => {
  return pageTitleInfo?.label
    ? `${pageTitleInfo?.label}`
    : ` ${t("common:label_with_home", {
        label: curriculumTypeName,
      })}`;
};

const getCoursesPageTitle = ({ pageTitleInfo, courseDetails, t }) => {
  return pageTitleInfo?.isLocalized
    ? `${courseDetails?.title} - ${pageTitleInfo?.label}`
    : `${courseDetails?.title} - ${t(pageTitleInfo?.label)}`;
};

const getLearningCoursesPageTitle = ({
  pageTitleInfo,
  learningCourseDetails,
  t,
}) => {
  const learningCourseTitle = _.get(learningCourseDetails, "title", "");
  const pageTitle = pageTitleInfo?.label;

  return pageTitleInfo?.isLocalized
    ? `${learningCourseTitle} - ${pageTitle}`
    : `${learningCourseTitle} - ${t(pageTitle)}`;
};

export const getPageTitle = ({
  pageTitleInfo,
  courseDetails,
  learningCourseDetails,
  curriculumTypeName,
  t,
}) => {
  switch (pageTitleInfo?.from) {
    case "courses":
      return getCoursesPageTitle({ pageTitleInfo, courseDetails, t });
    case "admin":
      return getAdminPageTitle({ pageTitleInfo, curriculumTypeName, t });
    case "home-page":
      return getHomePageTitle({ pageTitleInfo, curriculumTypeName, t });
    case "learning-course":
      return getLearningCoursesPageTitle({
        pageTitleInfo,
        learningCourseDetails,
        t,
      });
    default:
      return t(pageTitleInfo?.label);
  }
};
// ---------------------------------------------- END: academic year curriculum mapping ----------------------------------------------

export const isParentUserMenuRoutes = () => {
  // UserMenu redirects routes list for parent from TopHeader
  const userMenuRoutes = ["my-profile"];
  const currentUrl = window.location.href;
  return _.some(userMenuRoutes, route => currentUrl.includes(route));
};

/**
 * Purpose: To store number of open modals, when component supporting shortcuts is mounted.
 */
export const getModalCountOnMount = () => {
  return window.__OPEN_MODALS__.length;
};

/**
 * Purpose: To check whether shortcuts are valid or not on each keyboard event. It is checked like this:
 * If user is typing in an input like field, then shortcuts are invalid.
 * If component supporting shortcuts is mounted, but hidden behind a modal, then shortcuts are invalid.
 */
export const getShortcutValidityStatus = previousModalStackLengthForContainerVisibility => {
  if (isTypingInInput()) {
    return "INVALID";
  }

  const length = window.__OPEN_MODALS__.length;

  /**
   * If current number of modals, are greater than the previousModalStackLengthForContainerVisibility,
   * then obviously container is hidden by a modal.
   */
  if (length > previousModalStackLengthForContainerVisibility) {
    return "INVALID";
  }

  /**
   * If current number of modals are same or less then previousModalStackLengthForContainerVisibility,
   * we can enable shortcuts.
   */
  if (length <= previousModalStackLengthForContainerVisibility) {
    return "VALID";
  }

  return "INVALID";
};

export const getShortcutPreferenceValues = ({ userId, organizationId }) => {
  const areShortcutsEnabled =
    getSettingValue({
      name: ENABLE_ALL_SHORTCUTS,
      organizationId,
      userId,
      readFromUserAndOrgLevel: true,
    }) && ACLStore.can(ENABLE_GLOBAL_HEADER);

  const areShortcutPromptsEnabled = getSettingValue({
    name: ENABLE_SHORTCUT_PROMPTS,
    organizationId,
    userId,
    readFromUserAndOrgLevel: true,
  });

  return {
    areShortcutsEnabled,
    areShortcutPromptsEnabled,
  };
};
